import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { DataGrid, GridRowModes, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { apiUrl } from '../utils/api';
import { dataGridSx } from '../utils/dataGridStyles';
import {
  TextField,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  AlertColor,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ListAltIcon from '@mui/icons-material/ListAlt';


const toNumericId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const buildCategoryMap = (categories) => {
  const map = new Map();
  categories.forEach((category) => {
    const id = toNumericId(category?.category_id);
    if (id !== null) {
      map.set(id, category.name ?? null);
    }
  });
  return map;
};

const buildUnitMap = (units) => {
  const map = new Map();
  units.forEach((unit) => {
    const id = toNumericId(unit?.unit_id);
    if (id !== null) {
      map.set(id, unit);
    }
  });
  return map;
};

const normalizeBudgetLineItem = (item, categoryMap, unitMap) => {
  const categoryIdNumber = toNumericId(item?.category_id);
  const unitIdNumber = toNumericId(item?.unit_id);

  const normalizedCategoryId = categoryIdNumber === null ? '' : String(categoryIdNumber);
  const normalizedUnitId = unitIdNumber === null ? '' : String(unitIdNumber);

  const fallbackUnit = unitIdNumber !== null ? unitMap.get(unitIdNumber) : undefined;

  return {
    ...item,
    category_id: normalizedCategoryId,
    unit_id: normalizedUnitId,
    category_name:
      item?.category_name ??
      (categoryIdNumber !== null ? categoryMap.get(categoryIdNumber) ?? null : null),
    unit_code:
      item?.unit_code ??
      fallbackUnit?.unit_code ??
      null,
    unit_description:
      item?.unit_description ??
      fallbackUnit?.unit_description ??
      null,
  };
};

const BudgetLineItems = ({ access = 'none', projectId = null, deleteAllAccess }) => {
  const readOnly = access !== 'write';
  const effectiveDeleteAccess = deleteAllAccess ?? access;
  const canDeleteAll = effectiveDeleteAccess === 'write';
  const [deletingAll, setDeletingAll] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [budgetLineItems, setBudgetLineItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [budgetCategories, setBudgetCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowModesModel, setRowModesModel] = useState({});
  // Track the clicked BOQ row so selection is visibly highlighted.
  const [selectedRowId, setSelectedRowId] = useState<any>(null);
  const [newItem, setNewItem] = useState({
    item_code: '',
    item_description: '',
    category_id: '',
    unit_id: '',
    is_active: true
  });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [importSourceOpen, setImportSourceOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mapFile, setMapFile] = useState<File | null>(null);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const ITEM_FIELDS = ['Item Code', 'Item Description', 'Category Name', 'Unit Code', 'Active'];
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [excelImporting, setExcelImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importSummaryOpen, setImportSummaryOpen] = useState(false);
  const [importSummaryData, setImportSummaryData] = useState<any>(null);
  const fileInputRef = React.useRef(null);

  const showNotification = useCallback((message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const categoryMap = useMemo(() => buildCategoryMap(budgetCategories), [budgetCategories]);
  const unitMap = useMemo(() => buildUnitMap(units), [units]);

  const unitLabelById = useMemo(() => {
    const map = new Map();
    units.forEach((unit) => {
      const label = unit.unit_description
        ? `${unit.unit_code} - ${unit.unit_description}`
        : unit.unit_code;
      map.set(unit.unit_id, label);
    });
    return map;
  }, [units]);

  const categoryOptions = useMemo(() => {
    const options = budgetCategories.map((cat) => ({
      value: String(cat.category_id),
      label: cat.name
    }));
    const seen = new Set(options.map((option) => option.value));

    budgetLineItems.forEach((item) => {
      const value = (
        item?.category_id !== undefined && item?.category_id !== null
          ? String(item.category_id)
          : ''
      );
      if (!value || seen.has(value)) {
        return;
      }
      const label = item.category_name || `Category ${value}`;
      options.push({ value, label });
      seen.add(value);
    });

    return options;
  }, [budgetCategories, budgetLineItems]);

  const unitOptions = useMemo(() => {
    const options = units.map((unit) => ({
      value: String(unit.unit_id),
      label: unitLabelById.get(unit.unit_id) || unit.unit_code
    }));
    const seen = new Set(options.map((option) => option.value));

    budgetLineItems.forEach((item) => {
      const value = (
        item?.unit_id !== undefined && item?.unit_id !== null
          ? String(item.unit_id)
          : ''
      );
      if (!value || seen.has(value)) {
        return;
      }

      const numericValue = toNumericId(value);
      const label = item.unit_code
        ? (item.unit_description ? `${item.unit_code} - ${item.unit_description}` : item.unit_code)
        : unitLabelById.get(numericValue) || `Unit ${value}`;

      options.push({ value, label });
      seen.add(value);
    });

    return options;
  }, [units, unitLabelById, budgetLineItems]);

  useEffect(() => {
    if (access === 'none') {
      setBudgetLineItems([]);
      setBudgetCategories([]);
      setUnits([]);
      setLoading(false);
      return;
    }

    if (!projectId) {
      setBudgetLineItems([]);
      setBudgetCategories([]);
      setUnits([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);

        const [unitsRes, categoriesRes, itemsRes] = await Promise.all([
          fetch(apiUrl('/api/units')),
          fetch(apiUrl(`/api/budget-categories?project_id=${projectId}`)),
          fetch(apiUrl(`/api/budget-line-items?project_id=${projectId}`))
        ]);

        if (!unitsRes.ok) {
          throw new Error('Failed to fetch units');
        }
        if (!categoriesRes.ok) {
          throw new Error('Failed to fetch budget categories');
        }
        if (!itemsRes.ok) {
          throw new Error('Failed to fetch budget line items');
        }

        const [unitsData, categoriesData, itemsData] = await Promise.all([
          unitsRes.json(),
          categoriesRes.json(),
          itemsRes.json()
        ]);

        if (cancelled) {
          return;
        }

        setUnits(unitsData);
        setBudgetCategories(categoriesData);

        const categoryMapFromData = buildCategoryMap(categoriesData);
        const unitMapFromData = buildUnitMap(unitsData);
        const normalizedItems = itemsData.map((item) =>
          normalizeBudgetLineItem(item, categoryMapFromData, unitMapFromData)
        );

        setBudgetLineItems(normalizedItems);
      } catch (error) {
        console.error('Error initializing budget line items:', error);
        if (!cancelled) {
          showNotification('Failed to load BOQ line items', 'error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [access, projectId, showNotification]);

  useEffect(() => {
    if (!excelImporting) return;
    const timer = setInterval(() => {
      setImportProgress(p => (p < 90 ? p + 5 : p));
    }, 300);
    return () => clearInterval(timer);
  }, [excelImporting]);

  const refreshBudgetLineItems = useCallback(async () => {
    if (access === 'none' || !projectId) {
      setBudgetLineItems([]);
      return;
    }

    setLoading(true);
    try {
      let url = '/api/budget-line-items';
      if (projectId) {
        url += `?project_id=${projectId}`;
      }

      const response = await fetch(apiUrl(url));
      if (!response.ok) throw new Error('Failed to fetch budget line items');

      const data = await response.json();
      const normalizedItems = data.map((item) => normalizeBudgetLineItem(item, categoryMap, unitMap));
      setBudgetLineItems(normalizedItems);
    } catch (error) {
      console.error('Error fetching budget line items:', error);
      showNotification('Failed to load budget line items', 'error');
    } finally {
      setLoading(false);
    }
  }, [access, projectId, categoryMap, unitMap, showNotification]);

  const handleAdd = async () => {
    if (readOnly || addLoading || !newItem.item_description.trim() || !newItem.category_id || !newItem.unit_id) return;

    setAddLoading(true);
    try {
      const itemCode = newItem.item_code.trim();
      const payload = {
        item_code: itemCode || null,
        item_description: newItem.item_description.trim(),
        category_id: Number(newItem.category_id),
        unit_id: Number(newItem.unit_id),
        is_active: newItem.is_active
      };

      const response = await fetch(apiUrl('/api/budget-line-items'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to create budget line item');
      }

      setNewItem({
        item_code: '',
        item_description: '',
        category_id: '',
        unit_id: '',
        is_active: true
      });
      refreshBudgetLineItems();
      showNotification('BOQ line item created successfully');
    } catch (error) {
      console.error('Error creating budget line item:', error);
      showNotification(error.message, 'error');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteClick = useCallback((id) => () => {
    if (readOnly) return;
    const item = budgetLineItems.find(i => i.bli_id === id);
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  }, [readOnly, budgetLineItems]);

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      const response = await fetch(apiUrl(`/api/budget-line-items/${itemToDelete.bli_id}`), {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete budget line item');
      }

      refreshBudgetLineItems();
      showNotification('BOQ line item deleted successfully');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting budget line item:', error);
      showNotification(error.message, 'error');
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteAllClick = () => {
    if (!canDeleteAll) return;
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllConfirm = async () => {
    setDeletingAll(true);
    setLoading(true);

    try {
      const query = projectId ? `?project_id=${projectId}` : '';
      const response = await fetch(apiUrl(`/api/budget-line-items${query}`), {
        method: 'DELETE'
      });

      if (!response.ok) {
        let errorMessage = 'Failed to delete all budget line items';
        try {
          const errorData = await response.json();
          if (errorData && typeof errorData === 'object' && 'error' in errorData) {
            errorMessage = errorData.error || errorMessage;
          }
        } catch (parseError) {
          // Ignore JSON parse errors and use the default message
        }
        throw new Error(errorMessage);
      }

      await refreshBudgetLineItems();
      showNotification('All BOQ line items deleted');
      setDeleteAllDialogOpen(false);
    } catch (error) {
      console.error('Error deleting all budget line items:', error);
      showNotification(error.message, 'error');
      setLoading(false);
      setDeleteAllDialogOpen(false);
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDeleteAllCancel = () => {
    setDeleteAllDialogOpen(false);
  };

  // Export template
  const handleDownloadTemplate = () => {
    fetch(apiUrl(`/api/budget-line-items/template?project_id=${projectId}`))
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'boq_line_items_template.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => showNotification('Failed to download template', 'error'));
  };

  // Export to Excel
  const handleExport = () => {
    const XLSX = require('xlsx');
    const rows = budgetLineItems.map(item => ({
      'Item Code': item.item_code || '',
      'Item Description': item.item_description || '',
      'Category Name': item.category_name || '',
      'Unit Code': item.unit_code || '',
      'Active': item.is_active ? 'Yes' : 'No'
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'BOQ Line Items');
    XLSX.writeFile(wb, 'boq_line_items.xlsx');
  };

  // Import from Excel
  const handleImportClick = () => {
    if (readOnly) return;
    setImportSourceOpen(true);
  };

  const handleImportSource = (isThird: boolean) => {
    setImportSourceOpen(false);
    if (isThird) {
      setMapFile(null);
      setFileHeaders([]);
      setMapping({});
      setMappingOpen(true);
    } else { fileInputRef.current?.click(); }
  };

  const handleMapFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMapFile(f);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows: any = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
      const headers = ((rows as any)[0] || []).map((v: any) => String(v || ''));
      setFileHeaders(headers);
      const auto: Record<string, string> = { ...mapping };
      ITEM_FIELDS.forEach(fld => {
        const idx = headers.findIndex(h => h.toLowerCase().replace(/\s+/g, '_') === fld.toLowerCase().replace(/\s+/g, '_'));
        if (idx >= 0) auto[fld] = headers[idx];
      });
      setMapping(auto);
    } catch { setFileHeaders([]); }
  };

  const handleMappingSubmit = async () => {
    if (!mapFile) { alert('Select a file'); return; }
    try {
      const buf = await mapFile.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' }) as any[];
      const norm = rows.map(r => ITEM_FIELDS.reduce((obj, fld) => ({ ...obj, [fld]: (r as any)[mapping[fld]] ?? '' }), {}));
      const newWb = XLSX.utils.book_new();
      const newWs = XLSX.utils.json_to_sheet(norm);
      XLSX.utils.book_append_sheet(newWb, newWs, 'Sheet1');
      const buf2 = XLSX.write(newWb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf2], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const mappedFile = new File([blob], 'mapped.xlsx');
      setMappingOpen(false);
      await runImport(mappedFile);
    } catch (err: any) { alert(err.message || 'Mapping import failed'); }
  };

  const runImport = async (file: File) => {
    try {
      setExcelImporting(true);
      setImportProgress(0);
      const fd = new FormData();
      fd.append('file', file);
      const response = await fetch(apiUrl(`/api/budget-line-items/import?confirm=true&project_id=${projectId}&overwrite=true`), {
        method: 'POST',
        credentials: 'include',
        body: fd
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to import line items');
      const added = data.created || 0;
      const overwritten = data.updated || 0;
      const skipped = data.skipped || 0;
      const failed = (data.errors || []).length;
      const logRows = [
        ...Array(added).fill(null).map((_: any, i: number) => ({ label: `Item ${i + 1}`, status: 'Added' })),
        ...Array(overwritten).fill(null).map((_: any, i: number) => ({ label: `Item ${added + i + 1}`, status: 'Overwritten' })),
        ...Array(skipped).fill(null).map((_: any, i: number) => ({ label: `Existing Item ${i + 1}`, status: 'Skipped' })),
        ...(data.errors || []).map((e: any) => ({ label: `Row ${e.row || '?'}`, status: `Failed: ${e.field ? e.field + ' - ' : ''}${e.error || e}` }))
      ];
      setImportProgress(100);
      setImportSummaryData({ total: added + overwritten + skipped + failed, added, overwritten, skipped, failed, rows: logRows });
      setImportSummaryOpen(true);
      refreshBudgetLineItems();
    } catch (err: any) {
      setImportProgress(0);
      setImportSummaryData({ total: 0, added: 0, overwritten: 0, skipped: 0, failed: 1, error: err.message || 'Import failed', rows: [{ label: 'Import error', status: `Failed: ${err.message || 'Import failed'}` }] });
      setImportSummaryOpen(true);
      showNotification(err.message || 'Import failed', 'error');
    } finally { setExcelImporting(false); }
  };

  const downloadImportLog = (data: any) => {
    const logRows = (data.rows || []).map((r: any, i: number) => {
      const s = r.status || ''
      let status: string, notes: string
      if (s === 'Added') { status = 'Pass'; notes = 'New Item added' }
      else if (s === 'Overwritten') { status = 'Pass'; notes = 'Item overwritten' }
      else if (s === 'Skipped') { status = 'Pass'; notes = 'Existing kept (skipped)' }
      else { status = 'Failed'; notes = s.replace(/^Failed:\s*/i, '') }
      return { row_no: i + 2, item: r.label, status, notes }
    })
    fetch(apiUrl('/api/budget-line-items/import-log'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows: logRows })
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = 'BOQLineItem_Import_Log.xlsx'; a.click()
        window.URL.revokeObjectURL(url)
      })
      .catch(() => alert('Failed to generate import log'))
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    runImport(file);
  };

  const handleEditClick = useCallback((id) => () => {
    if (readOnly) return;
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  }, [readOnly, rowModesModel]);

  const handleSaveClick = useCallback((id) => () => {
    if (readOnly) return;
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  }, [readOnly, rowModesModel]);

  const handleCancelClick = useCallback((id) => () => {
    if (readOnly) return;
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    });
  }, [readOnly, rowModesModel]);

  const processRowUpdate = async (updatedRow) => {
    const { bli_id, item_code, item_description, category_id, unit_id, is_active } = updatedRow;

    const sanitizeId = (value) => {
      if (value === null || value === undefined || value === '') return null;
      const numeric = Number(value);
      return Number.isNaN(numeric) ? null : numeric;
    };

    const payload = {
      item_code: item_code?.trim() || null,
      item_description: item_description?.trim() || '',
      category_id: sanitizeId(category_id),
      unit_id: sanitizeId(unit_id),
      is_active
    };

    try {
      if (payload.category_id === null || payload.unit_id === null) {
        throw new Error('Category and Unit are required');
      }

      const response = await fetch(apiUrl(`/api/budget-line-items/${bli_id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to update budget line item');
      }

      refreshBudgetLineItems();
      showNotification('BOQ line item updated successfully');
      return normalizeBudgetLineItem({ ...updatedRow, ...responseData }, categoryMap, unitMap);
    } catch (error) {
      console.error('Error updating budget line item:', error);
      showNotification(error.message, 'error');
      throw error;
    }
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'bli_id',
      headerName: 'ID',
      width: 90,
      type: 'number'
    },
    {
      field: 'item_code',
      headerName: 'Item Code',
      flex: 1,
      editable: !readOnly,
      minWidth: 120
    },
    {
      field: 'item_description',
      headerName: 'Description',
      flex: 3,
      editable: !readOnly,
      minWidth: 250
    },
    {
      field: 'category_id',
      headerName: 'Category',
      flex: 1,
      minWidth: 160,
      editable: !readOnly,
      type: 'singleSelect',
      valueOptions: categoryOptions,
      valueGetter: (params: any) => {
        const rawValue = (params as any)?.row?.category_id;
        return rawValue !== undefined && rawValue !== null ? String(rawValue) : '';
      },
      renderCell: (params) => {
        const row = params.row || {};
        if (row.category_name) {
          return row.category_name;
        }
        const numericId = toNumericId(row.category_id);
        if (numericId === null) {
          return 'Unknown';
        }
        const fallback = categoryMap.get(numericId);
        return fallback || `Category ${numericId}`;
      }
    },

    {
      field: 'unit_id',
      headerName: 'Unit',
      width: 180,
      editable: !readOnly,
      type: 'singleSelect',
      valueOptions: unitOptions,
      valueGetter: (params: any) => {
        const rawValue = (params as any)?.row?.unit_id;
        return rawValue !== undefined && rawValue !== null ? String(rawValue) : '';
      },
      renderCell: (params) => {
        const row = params.row || {};
        if (row.unit_code) {
          return row.unit_description
            ? `${row.unit_code} - ${row.unit_description}`
            : row.unit_code;
        }
        const numericId = toNumericId(row.unit_id);
        if (numericId === null) {
          return 'Unknown';
        }
        const fallback = unitLabelById.get(numericId);
        return fallback || `Unit ${numericId}`;
      }
    },

    {
      field: 'is_active',
      headerName: 'Active',
      width: 100,
      type: 'boolean',
      editable: !readOnly,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      type: 'dateTime',
      valueGetter: (params: any) => {
        if (!params || !(params as any).value) return null;
        try {
          return new Date((params as any).value);
        } catch (e) {
          return null;
        }
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: ({ id }) => {
        if (readOnly) return [];

        const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isEditing) {
          return [
            <GridActionsCellItem
              icon={<Tooltip title="Save"><SaveIcon /></Tooltip>}
              label="Save"
              onClick={handleSaveClick(id)}
              showInMenu={false}
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>}
              label="Cancel"
              onClick={handleCancelClick(id)}
              showInMenu={false}
            />
          ];
        }

        return [
          <GridActionsCellItem
            icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
            label="Edit"
            onClick={handleEditClick(id)}
            showInMenu={false}
          />,
          <GridActionsCellItem
            icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>}
            label="Delete"
            onClick={handleDeleteClick(id)}
            showInMenu={false}
          />
        ];
      }
    }
  ], [categoryOptions, unitOptions, readOnly, rowModesModel, handleSaveClick, handleCancelClick, handleEditClick, handleDeleteClick, categoryMap, unitLabelById]);

  if (access === 'none') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to access this page.
        </Alert>
      </Box>
    );
  }

  if (!projectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a project to manage BOQ line items.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage standardized BOQ line items that serve as the foundation for BOQ, design changes,
        and contract budget lines across your project.
      </Typography>

      {!readOnly && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Add New BOQ Line Item
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
                <TextField
                  fullWidth
                  label="Item Code"
                  value={newItem.item_code}
                  onChange={(e) => setNewItem({ ...newItem, item_code: e.target.value })}
                  placeholder="Optional"
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={newItem.item_description}
                  onChange={(e) => setNewItem({ ...newItem, item_description: e.target.value })}
                  placeholder="e.g., Concrete Grade 30"
                  size="small"
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={newItem.category_id}
                    onChange={(e) => setNewItem({ ...newItem, category_id: e.target.value })}
                    label="Category"
                  >
                    {categoryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Unit</InputLabel>
                  <Select
                    value={newItem.unit_id}
                    onChange={(e) => setNewItem({ ...newItem, unit_id: e.target.value })}
                    label="Unit"
                  >
                    {unitOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6, md: 1.5 }}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={newItem.is_active}
                      onChange={(e) => setNewItem({ ...newItem, is_active: e.target.checked })}
                      size="small"
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAdd}
                  disabled={!newItem.item_description.trim() || !newItem.category_id || !newItem.unit_id || addLoading}
                  startIcon={addLoading ? <CircularProgress size={20} /> : <AddIcon />}
                  sx={{ height: '100%' }}
                >
                  {addLoading ? 'Adding...' : 'Add Item'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <DataGrid
            rows={budgetLineItems}
            columns={columns}
            getRowId={(row) => row.bli_id}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            processRowUpdate={processRowUpdate}
            loading={loading}
            onRowClick={(params) => setSelectedRowId(params.id)}
            rowSelectionModel={
              selectedRowId != null
                ? { type: 'include', ids: new Set([selectedRowId]) }
                : { type: 'include', ids: new Set() }
            }
            autoHeight
            columnVisibilityModel={{
              bli_id: false,
              created_at: false
            }}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 }
              }
            }}
            pageSizeOptions={[25, 50, 100]}
            sx={{
              ...dataGridSx,
              '& .MuiDataGrid-row': {
                ...dataGridSx['& .MuiDataGrid-row'],
                cursor: 'pointer'
              },
              '& .MuiSelect-select': {
                color: 'text.primary'
              },
              '& .MuiInputBase-input': {
                color: 'text.primary'
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Import/Export Buttons */}
      <Box sx={{
        mt: 2,
        mb: 2,
        display: 'flex',
        gap: 1,
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        alignItems: 'center'
      }}>
        <Button
          variant="outlined"
          onClick={handleDownloadTemplate}
          sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
        >
          Export Template
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
        >
          Export To Excel
        </Button>
        <Button
          variant="contained"
          onClick={handleImportClick}
          disabled={readOnly}
          sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
        >
          Import From Excel
        </Button>
        {canDeleteAll && (
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAllClick}
            disabled={deletingAll || loading}
            sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
          >
            {deletingAll ? 'Deleting...' : 'Delete All'}
          </Button>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
        />
      </Box>

      {/* Delete Item Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h6" fontWeight={600} color="error">
            Delete BOQ Line Item
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete the following item?
          </Typography>
          {itemToDelete && (
            <Box sx={{
              bgcolor: 'grey.50',
              p: 2,
              borderRadius: 2,
              border: 1,
              borderColor: 'grey.300'
            }}>
              {itemToDelete.item_code && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Item Code
                  </Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ mb: 1.5 }}>
                    {itemToDelete.item_code}
                  </Typography>
                </>
              )}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Description
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {itemToDelete.item_description}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={deleteAllDialogOpen}
        onClose={handleDeleteAllCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h6" fontWeight={600} color="error">
            Delete All BOQ Line Items
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1">
            Are you sure you want to delete <strong>ALL</strong> BOQ line items for this project?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will permanently remove all line items from the database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleDeleteAllCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Source Dialog */}
      <Dialog open={importSourceOpen} onClose={() => setImportSourceOpen(false)}>
        <DialogTitle>Import From Excel</DialogTitle>
        <DialogContent>Choose source type:</DialogContent>
        <DialogActions>
          <Button onClick={() => handleImportSource(false)}>Template</Button>
          <Button onClick={() => handleImportSource(true)}>Third-party (Map Columns)</Button>
          <Button onClick={() => setImportSourceOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Column Mapping Dialog */}
      <Dialog open={mappingOpen} onClose={() => { setMappingOpen(false); setFileHeaders([]); setMapFile(null); setMapping({}); }} maxWidth="md" fullWidth>
        <DialogTitle>Map Columns (Third-party Excel)</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: 8 }}>
            <input type="file" accept=".xlsx,.xls" onChange={handleMapFileChange} />
          </div>
          {!!fileHeaders.length && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {ITEM_FIELDS.map(f => (
                <div key={f}>
                  <div style={{ fontSize: 12, color: '#555' }}>{f}</div>
                  <TextField select size="small" fullWidth value={mapping[f] || ''} onChange={(e) => setMapping(prev => ({ ...prev, [f]: e.target.value }))}>
                    <MenuItem value="">(none)</MenuItem>
                    {fileHeaders.map(h => <MenuItem key={`${f}-${h}`} value={h}>{h}</MenuItem>)}
                  </TextField>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMappingOpen(false)}>Cancel</Button>
          <Button onClick={handleMappingSubmit} disabled={!mapFile}>Import</Button>
        </DialogActions>
      </Dialog>

      {/* Importing Progress Dialog */}
      <Dialog open={excelImporting}>
        <DialogTitle>Importing...</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <LinearProgress variant="determinate" value={importProgress} />
          <Typography sx={{ mt: 1 }} align="center">{importProgress}%</Typography>
        </DialogContent>
      </Dialog>

      {/* Import Summary Dialog */}
      <Dialog open={importSummaryOpen} onClose={() => setImportSummaryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{importSummaryData?.error ? 'Import Failed' : 'Import Summary'}</DialogTitle>
        <DialogContent>
          {importSummaryData && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>Total items processed: <strong>{importSummaryData.total}</strong></Typography>
              <Typography variant="body2" sx={{ color: 'green' }}>Added: {importSummaryData.added}</Typography>
              <Typography variant="body2" sx={{ color: 'goldenrod' }}>Overwritten: {importSummaryData.overwritten}</Typography>
              <Typography variant="body2" sx={{ color: 'grey' }}>Skipped: {importSummaryData.skipped}</Typography>
              <Typography variant="body2" sx={{ color: 'red', mb: 2 }}>Failed: {importSummaryData.failed}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total changes: {importSummaryData.added + importSummaryData.overwritten}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => downloadImportLog(importSummaryData)} variant="outlined">Download Import Log</Button>
          <Button onClick={() => setImportSummaryOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity as AlertColor}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BudgetLineItems;









